import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// Mapowanie priorytetu na kolor kropki. Backend bywa niespojny
// ('pilny'/'wazny'/'normalny' vs 'wysoki'/'normalny'/'niski'),
// wiec obejmujemy oba warianty + bezpieczny fallback na 'normalny'.
const priorityConfig = {
  pilny:    { dot: 'var(--danger)' },
  wysoki:   { dot: 'var(--danger)' },
  wazny:    { dot: 'var(--warning)' },
  normalny: { dot: 'var(--info)' },
  niski:    { dot: 'var(--text-muted)' },
};

// "Jan Kowalski" ma byc krotki; login moze byc dlugi
const shortText = (raw, max) => {
  if (!raw) return '';
  const s = String(raw);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const formatWhen = (raw) => {
  if (!raw) return '';
  try {
    const date = new Date(raw);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    if (sameDay) {
      return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
  } catch (e) {
    return '';
  }
};

const MessagesWidget = ({ userLogin, locationId }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadingRef = useRef(false);

  const load = useCallback(async () => {
    if (!userLogin) return;
    loadingRef.current = true;
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams();
      params.append('folder', 'inbox');
      params.append('user_login', userLogin);
      if (locationId) params.append('location_id', locationId);
      params.append('limit', 5);
      const res = await api.get(`/messages?${params}`);
      const list = res?.data?.data;
      setItems(Array.isArray(list) ? list.slice(0, 5) : []);
    } catch (e) {
      // Cichy blad - widget nie moze wywrocic POS
      setError(true);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [userLogin, locationId]);

  useEffect(() => { load(); }, [load]);

  // Auto-odswiezanie co 30s; pomijamy gdy karta w tle lub trwa juz load
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (loadingRef.current) return;
      load();
    }, 30000);
    return () => clearInterval(id);
  }, [load]);

  const unreadCount = items.reduce((acc, m) => acc + (m && !m.is_read ? 1 : 0), 0);

  const goToMessages = () => navigate('/wiadomosci');

  return (
    <div
      style={{
        backgroundColor: 'var(--card-bg)',
        borderLeft: '3px solid var(--info)',
        borderRadius: '4px',
        padding: '0.4rem 0.5rem',
        minWidth: '200px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--info)',
          marginBottom: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          Ostatnie wiadomości
          {unreadCount > 0 && (
            <span
              title={`Nieprzeczytane: ${unreadCount}`}
              style={{
                fontSize: '9px',
                background: 'var(--info)',
                color: '#fff',
                borderRadius: '8px',
                padding: '0 5px',
                fontWeight: 700,
              }}
            >
              {unreadCount}
            </span>
          )}
        </span>
        <button
          onClick={load}
          disabled={loading}
          title="Odśwież"
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            fontSize: '11px',
            padding: '0 2px',
            lineHeight: 1,
          }}
        >
          {loading ? '…' : '↻'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Brak wiadomości</div>
      )}
      {!error && items.length === 0 && !loading && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Brak wiadomości</div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          maxHeight: '78px',
          overflowY: 'auto',
          paddingRight: '2px',
        }}
      >
        {items.map((m) => {
          const cfg = priorityConfig[m.priorytet] || priorityConfig.normalny;
          const isUnread = !m.is_read;
          const sender = shortText(m.nadawca_login || 'system', 12);
          const title = shortText(m.tytul || '(bez tematu)', 22);
          const when = formatWhen(m.created_at);
          return (
            <div
              key={m.id}
              onClick={goToMessages}
              title={`${m.nadawca_login || 'system'}: ${m.tytul || ''}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '8px 1fr auto',
                gap: '4px',
                alignItems: 'center',
                fontSize: '10px',
                padding: '2px 3px',
                borderRadius: '3px',
                color: 'var(--text)',
                lineHeight: 1.3,
                cursor: 'pointer',
                fontWeight: isUnread ? 600 : 400,
              }}
            >
              <span
                title={isUnread ? 'Nieprzeczytana' : 'Przeczytana'}
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: isUnread ? cfg.dot : 'transparent',
                  border: isUnread ? 'none' : '1px solid var(--border, rgba(0,0,0,0.2))',
                  justifySelf: 'center',
                }}
              />
              <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                <span style={{ color: 'var(--text-muted)' }}>{sender}</span>
                {' · '}
                <span>{title}</span>
              </span>
              <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {when}
              </span>
            </div>
          );
        })}
      </div>

      <button
        onClick={goToMessages}
        style={{
          marginTop: '3px',
          padding: '2px 0',
          border: 'none',
          background: 'transparent',
          color: 'var(--info)',
          fontSize: '11px',
          cursor: 'pointer',
          textAlign: 'center',
          fontWeight: 600,
        }}
      >
        wszystkie wiadomości →
      </button>
    </div>
  );
};

export default MessagesWidget;
